import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  TextField,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Chip,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SEND_EVENT_PLAN_INVITATION } from './graphql';
import { useAuth } from '../../contexts/AuthContext';
import { enqueueSnackbar } from 'notistack';
import {
  SendEventPlanInvitationMutation,
  SendEventPlanInvitationMutationVariables,
} from '@pv/common/graphql/operations';
import { useEventPlan } from './useEventPlan';

export const EventPlanInvitationModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { guest } = useAuth();
  const { eventPlan } = useEventPlan();
  const [sendEventPlanInvitation] = useMutation<
    SendEventPlanInvitationMutation,
    SendEventPlanInvitationMutationVariables
  >(SEND_EVENT_PLAN_INVITATION, {
    onCompleted: (data) => {
      if (
        data?.sendEventPlanInvitation?.errors &&
        data.sendEventPlanInvitation.errors.length === 0
      ) {
        enqueueSnackbar('Invitation sent', {
          variant: 'success',
        });
      }
    },
  });

  const { collaborators, invitations } = eventPlan || {};

  const otherCollaborators = collaborators?.filter(
    (collaborator) => collaborator.email !== guest?.email,
  );

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    }),
    onSubmit: async (values) => {
      if (!eventPlan) return;
      const response = await sendEventPlanInvitation({
        variables: {
          input: {
            eventPlanId: eventPlan.id,
            inviteeEmail: values.email,
          },
        },
      });
      if (
        response.data?.sendEventPlanInvitation?.errors &&
        response.data.sendEventPlanInvitation.errors.length > 0
      ) {
        formik.setErrors({
          email: response.data.sendEventPlanInvitation.errors[0].message,
        });
      } else {
        handleClose();
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle
          sx={{
            padding: '20px',
            boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.10) inset',
          }}
        >
          Invite to {eventPlan?.name || 'Event'}
        </DialogTitle>
        <DialogContent sx={{ padding: '0 20px' }}>
          <Grid container spacing={2} padding="20px 0 0 0">
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    formik.handleSubmit();
                  }
                }}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          {otherCollaborators && otherCollaborators.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Collaborators
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {otherCollaborators.map((collaborator) => (
                  <Chip
                    key={collaborator.id}
                    avatar={
                      <Avatar
                        src={collaborator.gravatarUrl}
                        sx={{ width: 24, height: 24 }}
                      />
                    }
                    label={collaborator.fullName}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {invitations && invitations.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Invitations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {invitations.map((invitation) => (
                  <Chip
                    key={invitation.id}
                    label={invitation.inviteeEmail}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" color="secondary">
            Send
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
